/**
 * Route utilities and locale-aware wrappers for React Router APIs.
 *
 * When using dynamic route segment `:lang?` for locale prefix (instead of
 * dynamic basename), `useLocation().pathname` will include the locale prefix.
 * The wrappers ensure that pathname is always returned without the locale
 * prefix, making the code work consistently regardless of the routing approach.
 *
 * @example
 * // URL: /zh/generator/preset
 * // With dynamic basename: useLocation().pathname === '/generator/preset'
 * // With :lang? segment: useLocation().pathname === '/zh/generator/preset'
 * // With this wrapper: useLocation().pathname === '/generator/preset' (both cases)
 */

import debug from 'debug'
import type { ComponentType } from 'react'
import { type JSX, useMemo } from 'react'
import type { Params, NonIndexRouteObject } from 'react-router'
/* eslint-disable no-restricted-imports -- This is the wrapper module */
import {
  type Location,
  type ParamParseKey,
  type PathMatch,
  type PathPattern,
  redirect,
  type UIMatch,
  useLocation as useLocationOriginal,
  useMatches as useMatchesOriginal,
  useMatch as useMatchOriginal,
  useNavigation as useNavigationOriginal,
  useParams,
} from 'react-router'
/* eslint-enable no-restricted-imports */

import Cookies from 'js-cookie'
import { match, P } from 'ts-pattern'
import type { JsonValue } from 'type-fest'
import { withBase, withoutBase, withoutTrailingSlash, withQuery } from 'ufo'
import { DEFAULT_LOCALE, LOCALE_COOKIE_KEY } from 'src/i18n/constants'
import { isKnownLocale, normalizeLocale } from 'src/i18n/utils'
import { dynamic } from 'src/utils/ssr'

export interface AppRouteHandle {
  name: string
  track?:
    | Record<string, JsonValue | undefined>
    | ((ctx: { params: Params }) => Record<string, JsonValue | undefined>)
  hideMembershipBanner?: boolean
  /**
   * Only effective in pages wrapped by AppLayout
   */
  hideAppFooter?: boolean
  /**
   * Surfaces the credit pill in AppLayout header when true.
   */
  showCreditPill?: boolean
  /**
   * Show the app navigation sidebar when true.
   * Only effective in pages wrapped by AppLayout
   */
  showAppNav?: boolean
  /**
   * Bypass the client-only `<RootRuntime>` wrapper for this route's
   * subtree so the route's `default` Component is rendered directly
   * during SSR. The route is responsible for providing its own
   * route-scoped Apollo / QueryClient providers if it needs them.
   *
   * When combined with `ssrShellExperiment`, the SSR shell branch is
   * gated by the resolved experiment variant — see
   * `useResolvedSsrShell` in `src/utils/router/ssrShell.ts`.
   */
  ssrShell?: boolean
  /**
   * Optional Amplitude experiment that controls whether `ssrShell`
   * is honoured for the current request. When the resolved variant is
   * `'on'`, the route renders its Full SSR entry (route-scoped Apollo
   * seed + `initial*` props injected by loader). When the variant is
   * `'off'` (or missing), the route falls back to the CSR path under
   * `<RootRuntime>` (global Apollo / QueryClient), equivalent to
   * Stage 1 behaviour.
   *
   * Routes that are stable enough to enforce SSR unconditionally can
   * keep `ssrShell: true` without this field; the SSR branch will be
   * taken on every request.
   */
  ssrShellExperiment?: { name: string }
}

export interface AppRouteObject extends NonIndexRouteObject {
  id?: never // force no id to use default index id pattern
  handle: AppRouteHandle
  children?: AppRouteObject[]
}

/**
 * Strip the locale prefix from a pathname if present.
 *
 * @example
 * stripLocalePrefix('/zh/generator/preset') // '/generator/preset'
 * stripLocalePrefix('/generator/preset') // '/generator/preset'
 * stripLocalePrefix('/kr/artwork/123') // '/artwork/123'
 * stripLocalePrefix('/') // '/'
 */
export function stripLocalePrefix(pathname: string): string {
  // Early return for root, empty or relative path.
  if (pathname === '/' || pathname === '' || pathname.startsWith('.'))
    return pathname

  // Check if the first segment is a locale code
  const segments = pathname.split('/')
  const head = segments[1]?.toLowerCase()

  if (isKnownLocale(head)) {
    // Remove the locale segment and reconstruct the path
    const newPath = '/' + segments.slice(2).join('/')
    return newPath || '/'
  }

  return pathname
}

function getLocalePath(href: string, lang: unknown = DEFAULT_LOCALE): string {
  const locale = normalizeLocale(lang) ?? DEFAULT_LOCALE
  return withBase(href, `/${locale}`)
}

export function useLocalePath() {
  const params = useParams<{ lang?: string }>()
  const lang = params.lang
  return useMemo(() => (href: string) => getLocalePath(href, lang), [lang])
}

/**
 * A wrapper around `useLocation` that returns a location with the locale
 * prefix stripped from the pathname.
 *
 * This ensures consistent pathname handling regardless of whether the app
 * uses dynamic basename or `:lang?` route segment for locale routing.
 *
 * @example
 * // URL: /zh/generator/preset
 * const location = useLocation()
 * location.pathname // '/generator/preset' (in both routing approaches)
 */
export function useLocation(): Location {
  const location = useLocationOriginal()

  const resolveLocation = () => {
    const pathname = stripLocalePrefix(location.pathname)
    return { ...location, pathname }
  }

  return useMemo(resolveLocation, [location])
}

/**
 * Re-export of useLocation for cases where the original pathname with locale
 * prefix is needed. Prefer `useLocation` for most use cases.
 */
export { useLocationOriginal as useRouterLocation }

/**
 * A wrapper around `useMatches` that returns matches with the locale
 * prefix stripped from pathnames.
 *
 * @example
 * // URL: /zh/generator/preset
 * const matches = useMatches()
 * matches[0].pathname // '/generator/preset' (not '/zh/generator/preset')
 */
export function useMatches<Data = unknown, Handle = unknown>(): UIMatch<
  Data,
  Handle
>[] {
  const matches = useMatchesOriginal()

  const resolveMatches = () =>
    matches.map(match => {
      const pathname = stripLocalePrefix(match.pathname)
      return { ...match, pathname }
    }) as UIMatch<Data, Handle>[]

  return useMemo(resolveMatches, [matches])
}

/**
 * Re-export of useMatches for cases where the original pathname with locale
 * prefix is needed. Prefer `useMatches` for most use cases.
 */
export { useMatchesOriginal as useRouterMatches }

/**
 * A wrapper around `useMatch` that returns a match with the locale
 * prefix stripped from pathname and pathnameBase.
 *
 * @example
 * // URL: /zh/generator/preset
 * const match = useMatch('/generator/:tab')
 * match?.pathname // '/generator/preset' (not '/zh/generator/preset')
 */
export function useMatch<Path extends string>(
  pattern: PathPattern<Path> | Path,
): PathMatch<ParamParseKey<Path>> | null {
  const match = useMatchOriginal<Path>(pattern)

  const resolveMatch = () => {
    if (!match) return null
    const pathname = stripLocalePrefix(match.pathname)
    const pathnameBase = stripLocalePrefix(match.pathnameBase)
    return { ...match, pathname, pathnameBase }
  }

  return useMemo(resolveMatch, [match])
}

export { useMatchOriginal as useRouterMatch }

/**
 * A wrapper around `useNavigation` that returns a navigation state with the
 * locale prefix stripped from the location pathname (when location is defined).
 *
 * @example
 * // During navigation to /zh/generator/preset
 * const navigation = useNavigation()
 * navigation.location?.pathname // '/generator/preset' (not '/zh/generator/preset')
 */
export function useNavigation(): ReturnType<typeof useNavigationOriginal> {
  const navigation = useNavigationOriginal()

  const resolveNavigation = (): ReturnType<typeof useNavigationOriginal> => {
    if (!navigation.location) {
      return navigation
    }
    const pathname = stripLocalePrefix(navigation.location.pathname)
    return {
      ...navigation,
      location: { ...navigation.location, pathname },
    }
  }

  return useMemo(resolveNavigation, [navigation])
}

/**
 * Re-export of useNavigation for cases where the original pathname with locale
 * prefix is needed. Prefer `useNavigation` for most use cases.
 */
export { useNavigationOriginal as useRouterNavigation }

export function getRouteHandleFlag<Key extends keyof AppRouteHandle>(
  matches: ReturnType<typeof useMatches>,
  key: Key,
): AppRouteHandle[Key] | undefined {
  const match = matches.findLast(
    ({ handle }) =>
      handle &&
      typeof handle === 'object' &&
      key in handle &&
      handle[key as keyof typeof handle] !== undefined,
  )
  return match?.handle?.[key as keyof typeof match.handle]
}

/**
 * Check if any matched route has a specific handle property set to true
 */
export function useRouteHandleFlag<Key extends keyof AppRouteHandle>(
  key: Key,
): AppRouteHandle[Key] | undefined {
  const matches = useMatches()
  return getRouteHandleFlag(matches, key)
}

const log = debug('pixai:router:locale-prefix')

export interface ApplyLocalePrefixArgs {
  request: Request
  params: Record<string, string | undefined>
}

export function applyLocalePrefix(args: ApplyLocalePrefixArgs) {
  const { request, params } = args
  const url = new URL(request.url)
  const queryLang = url.searchParams.get('lang')?.toLowerCase()

  // Remove lang query param; it's consumed
  if (queryLang) {
    url.searchParams.delete('lang')
  }
  const normalizedQueryLang = normalizeLocale(queryLang)

  const paramsLang = params.lang
  const normalizedParamsLang = normalizeLocale(paramsLang)

  const cookieLang = normalizeLocale(
    Cookies.get(LOCALE_COOKIE_KEY)?.toLowerCase(),
  )

  // The order is intended to be different from localePrefix.middleware
  // Only fallback to read cookie when lang path is absent
  const newLang =
    normalizedQueryLang || normalizedParamsLang || cookieLang || DEFAULT_LOCALE

  // Without lang; like /generator/image, params.lang is 'generator'
  if (paramsLang && !normalizedParamsLang) {
    // skip stripping; nothing to strip
  } else if (paramsLang && paramsLang !== newLang) {
    // Strip paramsLang prefix whenever it doesn't match the resolved locale
    url.pathname = withoutBase(url.pathname, `/${paramsLang}`)
  }

  url.pathname = withoutTrailingSlash(withBase(url.pathname, `/${newLang}`))
  if (url.href !== request.url) {
    log('redirect from %s to %s, params: %o', request.url, url.href, params)
    throw redirect(url.href)
  } else {
    log('pass through %s, params: %o', request.url, params)
  }
}

// @see https://github.com/remix-run/react-router/issues/10475#issuecomment-1665464922
export async function passProps<T, P>(
  loader: Promise<T & { Component: ComponentType<P> }>,
  props: JSX.IntrinsicAttributes & P,
) {
  const mod = await loader
  return {
    ...mod,
    Component: undefined,
    element: <mod.Component {...props!} />,
  }
}
export function createRedirect(
  request: Request,
  to: string,
  query: Record<string, string | undefined> = {},
) {
  const originalQuery = Object.fromEntries(new URL(request.url).searchParams)
  return redirect(withQuery(to, { ...originalQuery, ...query }))
}

/**
 * It just extracts and moves the `handle` from `/:usernameRaw/:tab?/:subTab?` to here as is.
 * TODO: Should be removed once SSR migration is complete.
 */
export const userProfileHandle: AppRouteHandle = {
  name: 'profile',
  track: ({ params }) =>
    match(params)
      .with({ tab: 'tasks' }, () => ({
        scene: 'task-history',
        section: match(params.subTab)
          .with('favorites', () => 'favorite')
          .otherwise(() => 'all'),
      }))
      .with({ tab: 'credits' }, () => ({
        scene: 'member-credit-log',
      }))
      .with({ tab: 'following' }, () => ({ scene: 'following' }))
      .with({ tab: 'follower' }, () => ({ scene: 'followers' }))
      .with({ tab: 'decoration' }, () => ({
        scene: 'decoration',
        section: params.subTab,
      }))
      .otherwise(() => ({})),
  showAppNav: true,
}

export const userProfileLoader = async ({
  request,
  params,
}: {
  request: Request
  params: {
    lang?: string
    usernameRaw?: string
    tab?: string
    subTab?: string
  }
}) => {
  // Dynamically import client-only modules to avoid SSR issues
  const { DecorationPageTab, ProfilePageTab } =
    await import('src/components/UserProfile/constants')
  const { track } = await import('../tracker')

  match(params)
    .with({ usernameRaw: P.not(P.string.startsWith('@')) }, () => {
      throw createRedirect(request, `/${params.lang}/404`)
    })
    .with({ tab: 'likes', subTab: ProfilePageTab.likedSdv1Model }, () => {
      track('legacy_route', {
        pathname: `/@:username/likes/${ProfilePageTab.likedSdv1Model}`,
      })
      throw createRedirect(
        request,
        `/${params.usernameRaw}/${params.tab}/${ProfilePageTab.likedModel}`,
      )
    })
    .with({ tab: 'likes', subTab: ProfilePageTab.likedLoraModel }, () => {
      track('legacy_route', {
        pathname: `/@:username/likes/${ProfilePageTab.likedLoraModel}`,
      })
      throw createRedirect(
        request,
        `/${params.usernameRaw}/${params.tab}/${ProfilePageTab.likedLora}`,
      )
    })
    .with(
      {
        tab: ProfilePageTab.decorations,
        subTab: P.nullish.optional(),
      },
      () => {
        throw createRedirect(
          request,
          `/${params.usernameRaw}/${params.tab}/${DecorationPageTab.Badge}`,
        )
      },
    )
    .otherwise(() => {})
}

const DynamicUserProfilePage = dynamic(
  () => import('src/pages/UserProfilePage'),
  { ssr: false },
)

/**
 * Creates a user profile route with dynamic loading.
 * Uses element instead of lazy for SSR compatibility.
 */
export function createUserProfileRoute(tab: string): AppRouteObject {
  return {
    path: `${tab}/:subTab?`,
    element: <DynamicUserProfilePage tab={tab} />,
    loader: ({ params, ...rest }) =>
      userProfileLoader({ params: { ...params, tab }, ...rest }),
    handle: userProfileHandle,
    Component: undefined,
    lazy: undefined,
  }
}
